import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import {
  formatRelativeDue,
  resolveTemplateDueDate,
} from '../../lib/templateDueDate';
import type { PromotionPlan } from '../../types/promotionPlan';
import {
  applyTemplate,
  getTemplateForApply,
  listTemplatesForBrand,
} from './api';

function fmtAbs(iso: string | null): string {
  if (!iso) return 'No due date';
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
}

// §4.1/§4.2 Apply Template flow. One modal: pick a template (Global + this
// Plan's-brand only), preview the tasks with due dates resolved relative to
// the Plan's promoStartDate, then Apply. Existing tasks are kept (§4.2).
export function ApplyTemplateModal({
  plan,
  onClose,
  onApplied,
}: {
  plan: PromotionPlan;
  onClose: () => void;
  onApplied: (createdTaskCount: number) => void;
}) {
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const templatesQuery = useQuery({
    queryKey: ['taskTemplates', 'forBrand', plan.brandAccountId],
    queryFn: () => listTemplatesForBrand(plan.brandAccountId),
  });

  const previewQuery = useQuery({
    queryKey: ['taskTemplate', templateId, 'applyPreview'],
    queryFn: () => getTemplateForApply(templateId),
    enabled: !!templateId,
  });

  const mut = useMutation({
    mutationFn: () => applyTemplate(plan.id, templateId),
    onSuccess: (r) => onApplied(r.createdTaskCount),
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : String(err)),
  });

  const templates = templatesQuery.data ?? [];
  const preview = previewQuery.data ?? null;
  const noStart = !plan.promoStartDate;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-md border border-slate-200 bg-white shadow-xl">
        <div className="border-b border-slate-200 p-5">
          <h3 className="text-sm font-semibold text-slate-900">
            Apply a task template to “{plan.name}”
          </h3>
        </div>

        <div className="space-y-4 overflow-y-auto p-5">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">
              Template
            </span>
            {templatesQuery.isLoading ? (
              <Spinner />
            ) : templates.length === 0 ? (
              <p className="text-sm text-slate-500">
                No templates available for this brand. Create one in Settings
                → Task Templates.
              </p>
            ) : (
              <select
                value={templateId}
                onChange={(e) => {
                  setTemplateId(e.target.value);
                  setError(null);
                }}
                className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
              >
                <option value="">— Select a template —</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                    {t.scope === 'global' ? ' (Global)' : ''}
                  </option>
                ))}
              </select>
            )}
          </label>

          {templateId && previewQuery.isLoading && <Spinner />}

          {preview && (
            <div>
              <p className="text-sm text-slate-700">
                This will add{' '}
                <span className="font-medium">
                  {preview.items.length} task
                  {preview.items.length === 1 ? '' : 's'}
                </span>{' '}
                to this Plan. Existing tasks are kept.
              </p>

              {noStart ? (
                <p className="mt-2 rounded bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  This Plan has no promo start date — all tasks will be
                  created with <strong>no due date</strong>. Set a promo
                  start date on the Plan and re-apply if you need resolved
                  dates.
                </p>
              ) : (
                <p className="mt-2 text-xs text-slate-500">
                  Due dates resolved relative to the Plan’s promo start (
                  {fmtAbs(plan.promoStartDate?.slice(0, 10) ?? null)}):
                </p>
              )}

              {preview.items.length === 0 ? (
                <p className="mt-2 text-sm italic text-slate-400">
                  This template has no task items.
                </p>
              ) : (
                <ol className="mt-2 space-y-2">
                  {preview.items.map((it, idx) => {
                    const due = resolveTemplateDueDate(
                      plan.promoStartDate,
                      it.relativeDueDays,
                    );
                    return (
                      <li
                        key={it.id}
                        className="rounded border border-slate-200 p-2 text-sm"
                      >
                        <p className="font-medium text-slate-800">
                          {idx + 1}. {it.title}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          Due: {fmtAbs(due)}{' '}
                          <span className="text-slate-400">
                            ({formatRelativeDue(it.relativeDueDays)})
                          </span>{' '}
                          · Team: {it.defaultTeam ?? 'Unassigned'}
                        </p>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-5">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={mut.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              setError(null);
              mut.mutate();
            }}
            disabled={
              !templateId ||
              previewQuery.isLoading ||
              !preview ||
              mut.isPending
            }
          >
            {mut.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </div>
      </div>
    </div>
  );
}
